import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import KeyPlayersCard from '~/components/KeyPlayersCard/KeyPlayersCard';
import formatKeyPlayers, { getThreeStars } from '~/util/formatKeyPlayers';
import LineScoreCard from '~/components/LineScoreCard/LineScoreCard';
import MatchupComponent from '~/components/Matchup/Matchup';
import ScoringSummary from '~/components/ScoringSummary/ScoringSummary';
import Shootout from '~/components/Shootout/Shootout';
import TeamComparisonCard from '~/components/TeamComparisonCard/TeamComparisonCard';
import TeamStats from '~/components/TeamStats/TeamStats';
import TeamStatsGetTheApp from '~/components/TeamStatsGetTheApp/TeamStatsGetTheApp';
import GameDetailsCard from '~/components/GameDetailsCard/GameDetailsCard';
import StyledComponent from '~/components/Styled/Styled';
import MatchupSection from '~/components/MatchupSection/MatchupSection';
import PenaltySummary from '~/components/PenaltySummary/PenaltySummary';
import Ad, { AdTypes } from '~/containers/AdContainer/AdContainer';

import getActionGoals from '~/util/getActionGoals';
import getActionShootout from '~/util/getActionShootout';
import getOr from '~/util/getOr';
import getActionPenalties from '~/util/getActionPenalties';

import normalise from '~/schemas/teamStats/normalise';
import { NHL_PREGAME_OFFENSIVE, NHL_PREGAME_DEFENSIVE, NHL } from '~/schemas/teamStats/nhlSchema';

import { injectIntl, intlShape } from 'react-intl';

import messages from '~/constants/localizations/matchup';
import css from './NHLMatchup.scss';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';

/* eslint-disable camelcase */

class NHLMatchup extends React.Component {
  constructor() {
    super();

    this.state = {
      loading: false,
      goals: null,
      penalties: null,
      shootout: null,
    };
  }

  componentDidMount() {
    const { event_status } = this.props.event;

    this.setState({ loading: true });

    if (event_status === 'pre_game') {
      this.setState({ loading: false });

      return;
    }

    this.getGoalsAndPenaltiesData()
      .then(() => {
        this.setState({ loading: false });
      })
      .catch(() => {
        this.setState({ loading: false });
      });
  }

  componentDidUpdate(oldProps) {
    if (
      new Date(oldProps.event.updated_at).getTime() / 1000 !==
      new Date(this.props.event.updated_at).getTime() / 1000
    ) {
      this.getGoalsAndPenaltiesData();
    }
  }

  async getGoalsAndPenaltiesData() {
    const leagueSlug = this.props.event.league.slug;
    const id = this.props.event.box_score.id;
    const isShootout = this.props.event.box_score.progress.shootout;

    const goals = await getActionGoals(leagueSlug, id);
    const penalties = await getActionPenalties(leagueSlug, id);
    let shootout;

    const dataToDownload = [goals, penalties];

    if (isShootout) {
      shootout = await getActionShootout(leagueSlug, id);
      dataToDownload.push(shootout);
    }

    Promise.all([...dataToDownload]).then(values => {
      this.setState({
        goals: values[0],
        penalties: values[1].reverse(), // Reverse the Penalties data because it comes from the service with the latest registered penalties.
        shootout: values[2] || [],
      });
    });
  }

  renderMatchup() {
    return (
      <MatchupSection>
        <MatchupComponent event={this.props.event} />
      </MatchupSection>
    );
  }

  renderGameDetails() {
    return (
      <MatchupSection>
        <GameDetailsCard eventDetails={this.props.event.event_details} />
      </MatchupSection>
    );
  }

  renderTeamComparison() {
    return (
      <MatchupSection>
        <TeamComparisonCard event={this.props.event} />
      </MatchupSection>
    );
  }

  renderLineScore(title = null, nhlShots = false) {
    return (
      <MatchupSection>
        {title && <div className={css.header}>{title}</div>}
        <LineScoreCard event={this.props.event} nhlShots={nhlShots} />
      </MatchupSection>
    );
  }

  /* prettier-ignore */
  renderKeyPlayers() {
    const {
      intl: { formatMessage },
      event: { event_status, box_score },
    } = this.props;
    if (event_status === 'in_progress') return null;

    const goaltending = formatKeyPlayers(this.props.event);

    if (event_status !== 'final') {
      return (
        <MatchupSection>
          <KeyPlayersCard
            title={formatMessage(messages.goaltending)}
            stats={goaltending.stats}
            players={goaltending.players}
          />
        </MatchupSection>
      );
    }

    const threeStars = getThreeStars(box_score);
    return (
      <Fragment>
        <MatchupSection>
          <KeyPlayersCard
            title={formatMessage(messages.threeStars)}
            stats={threeStars.stats}
            players={threeStars.players}
            starPlayers
          />
        </MatchupSection>
        <MatchupSection>
          <KeyPlayersCard
            title={formatMessage(messages.goaltending)}
            stats={goaltending.stats}
            players={goaltending.players}
          />
        </MatchupSection>
      </Fragment>
    );
  }

  /* prettier-ignore */
  renderScoringSummaryAndShootout() {
    const {
      intl: { formatMessage },
      event
    } = this.props;

    const hasShootout = getOr(event, "box_score.progress.shootout", false)
    const awayTeamAbbreviation = getOr(event, "away_team.abbreviation", "N/A")
    const homeTeamAbbreviation = getOr(event, "home_team.abbreviation", "N/A")
    const renderGoals = this.state.goals && !this.state.loading
    const renderPenalties = this.state.penalties && !this.state.loading
    const renderShootout = hasShootout && this.state.shootout && !this.state.loading

    return (
      <Fragment>
        {this.state.loading ? <LoadingSpinner /> : null}

        {renderGoals? (
          <MatchupSection>
            <h2 className={css.header}>{formatMessage(messages.scoringSummary)}</h2>
            <ScoringSummary scorings={this.state.goals} />
          </MatchupSection>
        ) : null}
        {renderPenalties ? (
          <MatchupSection>
            <PenaltySummary penalties={this.state.penalties} />
          </MatchupSection>
        ) : null}
        {renderShootout ? (
          <MatchupSection>
            <div className={css.header}>{formatMessage(messages.shootout)}</div>
            <Shootout
              shootings={this.state.shootout}
              awayTeam={awayTeamAbbreviation}
              homeTeam={homeTeamAbbreviation}
            />
          </MatchupSection>
        ) : null}
      </Fragment>
    );
  }

  /* prettier-ignore */
  renderTeamStats() {
    const {
      intl: { formatMessage },
      event,
    } = this.props;
    const homeTeamName = getOr(event, 'home_team.abbreviation', 'N/A');
    const awayTeamName = getOr(event, 'away_team.abbreviation', 'N/A');
    const preGame = event.event_status === 'pre_game';

    if (preGame) {
      return (
        <Fragment>
          <div className={css.desktopView}>
            <MatchupSection>
              <div className={css.preGameStatsCard}>
                <div className={css.header}>{formatMessage(messages.offensiveStats)}</div>
                <TeamStats
                  homeTeamName={homeTeamName}
                  awayTeamName={awayTeamName}
                  stats={normalise(this.props.stats.offensive, NHL_PREGAME_OFFENSIVE)}
                />
              </div>
            </MatchupSection>
            <MatchupSection>
              <div className={css.preGameStatsCard}>
                <div className={css.header}>{formatMessage(messages.defensiveStats)}</div>
                <TeamStats
                  homeTeamName={homeTeamName}
                  awayTeamName={awayTeamName}
                  stats={normalise(this.props.stats.defensive, NHL_PREGAME_DEFENSIVE)}
                />
              </div>
            </MatchupSection>
          </div>

          <div className={css.mobileView}>
            <MatchupSection>
              <div className={css.preGameStatsCard}>
                <div className={css.header}>{formatMessage(messages.offensiveStats)}</div>
                <TeamStats
                  homeTeamName={homeTeamName}
                  awayTeamName={awayTeamName}
                  stats={normalise(this.props.stats.offensive, NHL_PREGAME_OFFENSIVE)}
                />
                {this.props.intl.locale === 'en' && (
                  <div className={`${css.mobileView} ${css.teamStatsGetTheApp}`}>
                    <TeamStatsGetTheApp leagueSlug="nhl" />
                  </div>
                )}
              </div>
            </MatchupSection>
          </div>
        </Fragment>
      );
    }

    return (
      <MatchupSection>
        <div className={css.teamStatsCard}>
          <h2 className={css.header}>{formatMessage(messages.teamStats)}</h2>
          <TeamStats
            homeTeamName={homeTeamName}
            awayTeamName={awayTeamName}
            stats={normalise(this.props.stats.team, NHL)}
          />
          {this.props.intl.locale === 'en' && (
            <div className={`${css.mobileView} ${css.teamStatsGetTheApp}`}>
              <TeamStatsGetTheApp leagueSlug="nhl" />
            </div>
          )}
        </div>
      </MatchupSection>
    );
  }

  /* prettier-ignore */
  render() {
    const {
      intl: { formatMessage },
      event: { event_status },
    } = this.props;
    const preGame = event_status === 'pre_game';
    const inProgress = event_status === 'in_progress';
    const leagueSlug = getOr(this.props.event, 'league.slug', '');

    return (
      <div className="row">
        <div className={`${css.column} col-sm-8`}>
          {this.renderMatchup()}
          <div className={css.desktopView}>
            {!preGame && this.renderScoringSummaryAndShootout()}
            {!this.state.loading ? this.renderTeamStats() : null}
            {preGame && <div className={css.mobileView}>{this.renderTeamComparison()}</div>}
          </div>
        </div>
        <div className="col-sm-4">
          <div className={css.desktopView}>
            {this.renderGameDetails()}
            <Ad leagueSlug={leagueSlug} adType={AdTypes.BIGBOX_DESKTOP} />
            {preGame && this.renderTeamComparison()}
            {!preGame && this.renderLineScore('Scoring')}
            {!inProgress && this.renderKeyPlayers()}
            {!preGame && this.renderLineScore(formatMessage(messages.shotsOnGoal), true)}
          </div>
        </div>
        <div className={css.mobileView}>
          {!preGame && this.renderLineScore('Scoring')}
          {!preGame && this.renderLineScore(formatMessage(messages.shotsOnGoal), true)}
          {!inProgress && this.renderKeyPlayers()}
          {!preGame && this.renderScoringSummaryAndShootout()}
          {!this.state.loading ? this.renderTeamStats() : null}
          {(inProgress || preGame) && this.renderTeamComparison()}
          {this.renderGameDetails()}
        </div>
      </div>
    );
  }
}

NHLMatchup.propTypes = {
  event: PropTypes.shape({
    home_team: PropTypes.object,
    away_team: PropTypes.object,
    event_status: PropTypes.string,
    event_details: PropTypes.array,
    box_score: PropTypes.shape({
      id: PropTypes.number,
      progress: PropTypes.shape({
        shootout: PropTypes.bool,
      }),
    }),
    league: PropTypes.shape({
      slug: PropTypes.string,
    }),
    home_stats_groups: PropTypes.array,
    away_stats_groups: PropTypes.array,
    updated_at: PropTypes.string,
  }).isRequired,
  intl: intlShape.isRequired,
  stats: PropTypes.object.isRequired,
};

NHLMatchup.displayName = 'NHLMatchup';

export default injectIntl(StyledComponent(NHLMatchup, [css]));
